import {ChangeDetectionStrategy, Component, computed, DoCheck, effect, inject, input, InputSignal, linkedSignal, output, OutputEmitterRef, Signal, signal, WritableSignal} from '@angular/core';
import {CalendarEventResource} from '../../../shared/types/generated/calendarEventResource';
import {DialogModule} from "@syncfusion/ej2-angular-popups";
import {DialogService} from '../../../shared/services/dialog.service';
import {HeadlineComponent} from "../../../shared/elements/headline/headline.component";
import {TooltipComponent} from "../../../shared/elements/tooltip/tooltip.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {ProfileService} from '../../../shared/services/profile.service';
import {CalendarStore} from '../stores/calendar-store';
import {DatePipe, TitleCasePipe} from '@angular/common';
import {endTimeCalculation} from '../mapper/calendar-helper.mapper';
import {ApiService} from '../../../shared/services/api.service';
import {AvatarComponent} from "../../../shared/elements/avatar/avatar.component";
import {CalendarEventConfirmation} from '../../../shared/types/generated/calendarEventConfirmation';
import {UserMinimalResource} from '../../../shared/types/generated/userMinimalResource';
import {LanguageService} from '../../../shared/services/language.service';
import {take} from 'rxjs';
import {GroupMinimalResource} from '../../../shared/types/generated/groupMinimalResource';
import {FormButtonComponent} from "../../../shared/elements/form-button/form-button.component";
import {ContentComponent} from "../../../shared/elements/content/content.component";
import {LinkifyPipe} from "../../../shared/pipes/linkify.pipe";
import {ItemDocumentsPreviewAvailableComponent} from "../../documents/item-documents-preview-available/item-documents-preview-available.component";
import {CalendarEventParticipantResource} from '../../../shared/types/generated/calendarEventParticipantResource';
import {ToasterService} from '../../../shared/services/toaster.service';
import {SocketService} from '../../../shared/services/socket.service';
import {UserContactResource} from '../../../shared/types/generated/userContactResource';

@Component({
  selector: 'app-dialog-calendar-event-details',
  imports: [DialogModule, HeadlineComponent, TooltipComponent, TranslatePipe, DatePipe, TitleCasePipe, AvatarComponent, FormButtonComponent, ContentComponent, LinkifyPipe, ItemDocumentsPreviewAvailableComponent],
  templateUrl: './dialog-calendar-event-details.component.html',
  styleUrl: './dialog-calendar-event-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogCalendarEventDetailsComponent implements DoCheck {
  public dialogService: DialogService = inject(DialogService);
  public calendarStore: InstanceType<typeof CalendarStore> = inject(CalendarStore);
  private profile: ProfileService = inject(ProfileService);
  private apiService: ApiService = inject(ApiService);
  private lang: LanguageService = inject(LanguageService);
  private toaster: ToasterService = inject(ToasterService);
  private socketService: SocketService = inject(SocketService);

  private loadedCreatorUserId: number | null | undefined = undefined;
  private loadedParticipantKey: string | null = null;
  private loadedParticipantsListEventId: number | null = null;

  public calendarEvent: InputSignal<CalendarEventResource> = input.required<CalendarEventResource>();
  public visible: InputSignal<boolean> = input.required<boolean>();
  public visibleChange: OutputEmitterRef<boolean> = output<boolean>();
  public creator: WritableSignal<UserContactResource | null> = signal(null);
  public participantUsers: WritableSignal<Record<number, UserContactResource>> = signal({});
  public participantGroups: WritableSignal<GroupMinimalResource[]> = signal([]);
  public participantsList: WritableSignal<CalendarEventParticipantResource[]> = signal([]);
  public rsvpOptions: CalendarEventConfirmation[] = [
    CalendarEventConfirmation.YES,
    CalendarEventConfirmation.MAYBE,
    CalendarEventConfirmation.NO,
  ];

  public creatorMode: Signal<boolean> = linkedSignal(() => {
    return this.event().permission.editable;
  });

  public personalRsvp: WritableSignal<CalendarEventConfirmation | null> = linkedSignal(() => {
    const currentUserId = this.profile.profile()?.user.id;
    if (!currentUserId) {
      return null;
    }

    return this.participantsList().find((participant) => participant.user.id === currentUserId)?.answer ?? null;
  });

  public hasPersonalRsvpResponse: Signal<boolean> = computed(() => {
    const currentUserId = this.profile.profile()?.user.id;
    if (!currentUserId) {
      return false;
    }

    return this.participantsList().some((participant) => participant.user.id === currentUserId);
  });

  public title: Signal<string> = computed(() => {
    if(this.calendarStore.translate() && this.event().name_translated !== null) {
      return String(this.event().name_translated);
    } else if(this.event().name !== null) {
      return this.event().name as string;
    } else {
      return '';
    }
  });
  
  public description: Signal<string> = computed(() => {
    if(this.calendarStore.translate() && this.event().description_translated !== null) {
      return String(this.event().description_translated);
    } else if(this.event().description !== null) {
      return this.event().description as string;
    } else {
      return '';
    }
  });

  public endTime: Signal<Date> = computed(() =>
    endTimeCalculation(
      new Date(this.event().timing.start_at),
      this.event().timing.duration,
      this.event().timing.is_full_day,
    )
  );

  public location: Signal<string> = computed(() => {
    if(this.calendarStore.translate() && this.event().location_translated !== null) {
      return String(this.event().location_translated);
    } else if(this.event().location !== null) {
      return this.event().location as string;
    } else {
      return '-';
    }
  });

  public formatDuration(durationInMinutes: number): string {
    const totalMinutes = Math.max(0, Math.floor(durationInMinutes));
    const days = Math.floor(totalMinutes / (24 * 60));
    const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const minutes = totalMinutes % 60;
    const parts: string[] = [];

    if (days > 0) {
      parts.push(`${days} ${days === 1 ? this.lang.getTranslationByKey('DAY') : this.lang.getTranslationByKey('DAYS')}`);
    }

    if (hours > 0) {
      parts.push(`${hours} ${hours === 1 ? this.lang.getTranslationByKey('HOUR') : this.lang.getTranslationByKey('HOURS')}`);
    }

    if (minutes > 0 || parts.length === 0) {
      parts.push(`${minutes} ${minutes === 1 ? this.lang.getTranslationByKey('MINUTE') : this.lang.getTranslationByKey('MINUTES')}`);
    }

    return parts.join(', ');
  }

  private resolvedEvent: Signal<{ event: CalendarEventResource; calendarId: string | number; } | null> = computed(() =>
    this.calendarStore.resolveEventWithSourceCalendar(this.calendarEvent().id, this.calendarEvent().calendar_id)
  );

  public resolvedCalendarId: Signal<number | string | null> = computed(() =>
    this.resolvedEvent()?.calendarId ?? this.calendarEvent().calendar_id ?? null
  );

  public event: Signal<CalendarEventResource> = computed(() => {
    return this.resolvedEvent()?.event ?? this.calendarEvent();
  });

  public calendarAllowsRSVP: Signal<boolean> = computed(() => {
    const calendar = this.calendarStore.calendars().find((x) => x.id === this.resolvedCalendarId());
    if(calendar) {
      return calendar.config.allow_confirmations;
    } else {
      return false;
    }
  });

  public eventAllowsRSVP: Signal<boolean> = computed(() =>
    this.event().confirmation?.allowed === true
  );

  public hasParticipantsSidebar: Signal<boolean> = computed(() =>
    !!(
      this.event().participants.user_ids.length ||
      this.event().participants.group_ids.length ||
      this.event().participants.all_users
    )
  );

  public ngDoCheck(): void {
    const creatorUserId = this.event().creator.user_id;
    if (this.loadedCreatorUserId !== creatorUserId) {
      this.loadedCreatorUserId = creatorUserId;
      this.loadCreator(creatorUserId);
    }

    const participantKey = JSON.stringify({
      user_ids: this.event().participants.user_ids,
      group_ids: this.event().participants.group_ids,
    });

    if (this.loadedParticipantKey !== participantKey) {
      this.loadedParticipantKey = participantKey;
      this.loadParticipants();
    }

    const eventId = this.visible() ? this.event().id : null;
    if (this.loadedParticipantsListEventId !== eventId) {
      this.loadedParticipantsListEventId = eventId;
      this.loadParticipantsList();
    }
  }

  public getCreatorName(): string {
    const creator = this.creator();
    if (!creator) {
      return '';
    }

    return `${creator.last_name} ${creator.first_name}`.trim();
  }

  public getCreatorAvatar(): string {
    return this.creator()?.avatar?.url?.responsive ?? '';
  }

  public participantUser(userId: number): UserContactResource | null {
    return this.participantUsers()[userId] ?? null;
  }

  public participantUserName(userId: number): string {
    const user = this.participantUser(userId);
    return user ? `${user.last_name} ${user.first_name}`.trim() : `#${userId}`;
  }

  public participantUserAvatar(userId: number): string {
    return this.participantUser(userId)?.avatar?.url?.responsive ?? '';
  }

  public participantGroup(groupId: number): GroupMinimalResource | null {
    return this.participantGroups().find((x) => x.id === groupId) ?? null;
  }

  public participantGroupName(groupId: number): string {
    return this.participantGroup(groupId)?.name ?? `#${groupId}`;
  }

  public participantGroupAvatar(groupId: number): string {
    return this.participantGroup(groupId)?.avatar?.url?.responsive ?? '';
  }

  public participantListName(participant: UserMinimalResource): string {
    return `${participant.last_name} ${participant.first_name}`.trim();
  }

  public creatorAnswer(): CalendarEventConfirmation | null {
    const creatorUserId = this.event().creator.user_id;
    if (!creatorUserId) {
      return null;
    }

    return this.participantsList().find((participant) => participant.user.id === creatorUserId)?.answer ?? null;
  }

  public participantEntryName(participant: CalendarEventParticipantResource): string {
    return this.participantListName(participant.user);
  }

  public canResetParticipantAnswer(participant: CalendarEventParticipantResource): boolean {
    const currentUserId = this.profile.profile()?.user.id;

    return participant.answer === CalendarEventConfirmation.NO && participant.user.id !== currentUserId;
  }

  public setPersonalRsvp(answer: CalendarEventConfirmation): void {
    if (this.isRsvpDisabled(answer)) {
      this.toaster.warning(
        this.lang.getTranslationByKey('WARNING'),
        this.lang.getTranslationByKey('CALDETAILEVENTCAPACITY')
      );
      return;
    }

    const previousRsvp = this.personalRsvp();
    this.calendarStore.setRsvpAnswer(
      this.event().id,
      this.resolvedCalendarId(),
      answer,
      () => this.personalRsvp.set(previousRsvp)
    );
    this.personalRsvp.set(answer);
    if(answer === CalendarEventConfirmation.NO) {
      this.visibleChange.emit(false);
    }
  }

  public resetParticipantAnswer(userId: number): void {
    this.apiService.resetCalendarEventUserResponse(this.event().id, userId)
    .pipe(take(1))
    .subscribe({
      next: () => {
        this.toaster.success(
          this.lang.getTranslationByKey('SUCCESS'),
          this.lang.getTranslationByKey('CALRESPONSERESETSUCCESS')
        );
        this.loadParticipantsList();
      }
    });
  }

  public participantConfirmation(userId: number): CalendarEventConfirmation | null {
    console.log(userId);
    // return this.calendarEvent().confirmations.find((confirmation) => confirmation.user_id === userId)?.answer ?? null;
    return null;
  }

  public rsvpButtonClass(
  answerType: CalendarEventConfirmation | null,
  answer: CalendarEventConfirmation | null
  ): string {
    switch (answerType) {
      case CalendarEventConfirmation.YES:
        return 'e-verysmall ' + (answer === null ? 'e-success' : answer === answerType ? 'e-success' : '');
      case CalendarEventConfirmation.NO:
        return 'e-verysmall ' + (answer === null ? 'e-danger' : answer === answerType ? 'e-danger' : '');
      case CalendarEventConfirmation.MAYBE:
        return 'e-verysmall ' + (answer === null ? 'e-warning' : answer === answerType ? 'e-warning' : '');
      default:
        return '';
    }
  }

  public isRsvpDisabled(answer: CalendarEventConfirmation): boolean {
    return answer === CalendarEventConfirmation.YES && this.event().confirmation?.capacity_limit_reached === true;
  }

  public rsvpBadgeClass(answer: CalendarEventConfirmation | null): string {
    switch (answer) {
      case CalendarEventConfirmation.YES:
        return 'bg-success-subtle text-success';
      case CalendarEventConfirmation.NO:
        return 'bg-danger-subtle text-danger';
      case CalendarEventConfirmation.MAYBE:
        return 'bg-warning-subtle text-warning';
      default:
        return 'text-muted';
    }
  }

  public rsvpLabel(answer: CalendarEventConfirmation | null): string {
    switch (answer) {
      case CalendarEventConfirmation.YES:
        return this.lang.getTranslationByKey('CALDETAILRVSPYES');
      case CalendarEventConfirmation.NO:
        return this.lang.getTranslationByKey('CALDETAILRVSPNO');
      case CalendarEventConfirmation.MAYBE:
        return this.lang.getTranslationByKey('CALDETAILRVSPMAYBE');
      default:
        return this.lang.getTranslationByKey('CALDETAILRVSPPENDING');
    }
  }

  private loadCreator(userId: number | null): void {
    if (!userId) {
      this.creator.set(null);
      return;
    }
    this.apiService.getContact(userId)
    .pipe(take(1))
    .subscribe({
      next: (creator) => {
        this.creator.set(creator);
      },
      error: () => {
        this.creator.set(null);
      }
    });
  }

  private loadParticipants(): void {
    this.participantUsers.set({});
    this.participantGroups.set([]);

    for (const userId of this.event().participants.user_ids) {
      this.apiService.getContact(userId)
        .pipe(take(1))
        .subscribe({
        next: (user) => {
          this.participantUsers.update((users) => ({...users, [userId]: user}));
        },
      })
    }

    this.apiService.getGroupsByIds(this.event().participants.group_ids)
    .pipe(take(1))
    .subscribe({
      next: (groups) => {
        this.participantGroups.set(groups);
      }
    })
  }

  public orderedParticipants: WritableSignal<CalendarEventParticipantResource[]> = linkedSignal(() => {
    return [...this.participantsList()].sort((a, b) => {
      const aName = `${a.user.last_name} ${a.user.first_name}`.trim();
      const bName = `${b.user.last_name} ${b.user.first_name}`.trim();

      return aName.localeCompare(bName);
    });
  })

  public constructor() {
    effect(() => {
      const updatedEvent = this.socketService.calendarEventUpdated();
      if(!updatedEvent) {
        return;
      }

      if (
        !this.visible()
        || this.event().id !== updatedEvent.eventId
        || this.resolvedCalendarId()?.toString() !== updatedEvent.calendarId.toString()
      ) {
        return;
      }

      this.refreshEventData();
      this.loadParticipantsList();
    });
  }

  private loadParticipantsList(): void {
    const eventId = this.event().id;
    if (!this.visible() || !eventId) {
      this.participantsList.set([]);
      return;
    }

    this.apiService.getCalendarEventParticipants(eventId)
    .pipe(take(1))
    .subscribe({
      next: (participants) => {
        this.participantsList.set(participants);
      },
      error: () => {
        this.participantsList.set([]);
      }
    });
  }

  private refreshEventData(): void {
    const calendarId = this.resolvedCalendarId();
    if (calendarId === null) {
      return;
    }

    this.calendarStore.loadCalendarEventsForCalendars(
      [calendarId],
      new Date(this.event().timing.start_at),
      this.endTime()
    );
  }

}
